import { FC, MouseEvent } from "react";

import { useAssessmentMode } from "../../../../../utils/assessment";

import { AssessmentStatus } from "api/api-data-types";
import { getAssessmentStatusLabel } from "components/materials/section/assessment/utils";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useSectionCapabilitiesBySectionId } from "store/capability-hooks";

export type AssessmentStatusChipProps = {
  page: PageType;
  assessmentStatus?: AssessmentStatus;
  unassessedLateCount?: number;
  assessedSubmissionsCount?: number;
};

export const AssessmentStatusChip: FC<AssessmentStatusChipProps> = ({
  page,
  assessmentStatus,
  unassessedLateCount,
  assessedSubmissionsCount,
}) => {
  const { startAssessing } = useAssessmentMode(page);
  const { canAuthorSection } = useSectionCapabilitiesBySectionId(page.section_id);

  // Shouldn't happen but we want to be safe
  if (assessmentStatus === AssessmentStatus.NOT_ASSESSABLE || !assessmentStatus) {
    return null;
  }

  const canAssess = assessmentStatus === AssessmentStatus.ASSESSABLE;

  const handleStartAssessing = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    startAssessing();
  };

  const statusText = getAssessmentStatusLabel(
    !!page.assessing_marked_complete_at,
    assessedSubmissionsCount || 0,
    assessmentStatus === AssessmentStatus.ASSESSED,
  );

  const showTooltip = !!unassessedLateCount && unassessedLateCount > 0 && canAuthorSection;

  // Note that we hide the Tooltip for students for now, but may
  // want to show a different message to students in the future.
  return (
    <Tooltip
      text={
        showTooltip
          ? t("assessment.late_assessment_count", { count: unassessedLateCount })
          : undefined
      }
    >
      <SquareButton
        className={!canAssess ? "cursor-default" : undefined}
        eventName="Assessment - Start"
        eventProps={{ pageTitle: page.title, pageId: page.id }}
        kind={page.assessing_marked_complete_at || !canAssess ? "complete" : "accent"}
        size="xs"
        onClick={canAssess ? handleStartAssessing : undefined}
      >
        {statusText}
      </SquareButton>
    </Tooltip>
  );
};
